import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateDebitNote } from '../utils/debitNote';
import { generateReportPdf } from '../utils/reportPdf';
import '../styles/Dashboard.css';

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [proposalStep, setProposalStep] = useState(1);
  const [premiumBreakdown, setPremiumBreakdown] = useState(null);
  const [proposalForm, setProposalForm] = useState({ cargoType: '', freightType: '', coverOption: '', insuredItem: '', sumInsured: '', coverPeriod: '', commencementDate: '', expiryDate: '', description: '', clientOID: '' });
  const [insuredItems, setInsuredItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  // Step 3 — client search / creation
  const [clientSearch, setClientSearch] = useState('');
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [newClient, setNewClient] = useState({ identificationNo: '', firstName: '', otherNames: '', surname: '', pin: '', mobile: '', email: '' });
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [submittingProposal, setSubmittingProposal] = useState(false);
  const [editingProposalOID, setEditingProposalOID] = useState(null);
  const [proposalResult, setProposalResult] = useState(null);
  const [proposalPayload, setProposalPayload] = useState(null);
  const [payingProposal, setPayingProposal] = useState(false);
  const [proposalsList, setProposalsList] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [proposalsRefresh, setProposalsRefresh] = useState(0);
  const [payingProposalId, setPayingProposalId] = useState(null);
  const [proposalSearch, setProposalSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [policiesList, setPoliciesList] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [policySearch, setPolicySearch] = useState('');
  const [policyDateFrom, setPolicyDateFrom] = useState('');
  const [policyDateTo, setPolicyDateTo] = useState('');
  const [policyDetails, setPolicyDetails] = useState(null);
  const [loadingPolicyDetails, setLoadingPolicyDetails] = useState(false);
  const [showPolicyDetails, setShowPolicyDetails] = useState(false);
  const [documentFolders, setDocumentFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [openFolder, setOpenFolder] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [folderSearch, setFolderSearch] = useState('');
  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileDocs, setProfileDocs] = useState([]);
  const [uploadingProfileDocs, setUploadingProfileDocs] = useState(false);
  const [profileRefresh, setProfileRefresh] = useState(0);
  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [overviewYear, setOverviewYear] = useState(new Date().getFullYear());
  const [overviewMonth, setOverviewMonth] = useState(new Date().getMonth() + 1);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [emailAlerts, setEmailAlerts] = useState(() => localStorage.getItem('emailAlerts') !== 'false');
  const [smsAlerts, setSmsAlerts] = useState(() => localStorage.getItem('smsAlerts') === 'true');
  const [toast, setToast] = useState(null);
  const [checkingApproval, setCheckingApproval] = useState(false);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  // Navigate to a tab and close the mobile drawer (no-op visually on desktop)
  const navTo = (tab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  };

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Verify the logged-in dealer is approved before allowing a new proposal
  const handleCreateProposalClick = async () => {
    if (!user?.oid) {
      showToast('Agency not approved, contact support');
      return;
    }

    setCheckingApproval(true);
    try {
      const response = await fetch(`https://uat.definiteassurance.com/api/marine/dealer-approval?oid=${encodeURIComponent(user.oid)}`);
      const data = await response.json();

      if (data.success && Number(data.approved) === 1) {
        setProposalStep(1);
        setPremiumBreakdown(null);
        setShowProposalForm(true);
      } else {
        showToast('Agency not approved, contact support');
      }
    } catch (error) {
      console.error('Error checking approval:', error);
      showToast('Unable to verify approval status. Please try again.');
    } finally {
      setCheckingApproval(false);
    }
  };

  const handleMenuClick = (menuName) => {
    if (expandedMenu === menuName) {
      setExpandedMenu(null);
    } else {
      setExpandedMenu(menuName);
    }
  };

  // Product Cover Options depend on cargo type.
  // ICC(AIR) applies to both Containerized and Non-Containerized.
  const iccAir = { label: 'ICC(AIR)', value: '3358' };
  const coverOptionsByCargoType = {
    Containerized: [
      { label: 'ICC(A)', value: '3352' },
      { label: 'ICC(B)', value: '3353' },
      { label: 'ICC(C)', value: '3354' },
      iccAir
    ],
    'Non-Containerized': [
      { label: 'ICC(A)', value: '3355' },
      { label: 'ICC(B)', value: '3356' },
      { label: 'ICC(C)', value: '3356' },
      iccAir
    ]
  };

  const handleProposalTypeChange = (cargoType) => {
    // Reset cover option and downstream fields when cargo type changes (keep freightType)
    setProposalForm((prev) => ({ ...prev, cargoType, coverOption: '', insuredItem: '', sumInsured: '', coverPeriod: '', commencementDate: '', expiryDate: '', description: '' }));
    setInsuredItems([]);
  };

  const closeProposalForm = () => {
    setShowProposalForm(false);
    setProposalStep(1);
    setEditingProposalOID(null);
    setPremiumBreakdown(null);
    setProposalResult(null);
    setProposalPayload(null);
    setProposalForm({ cargoType: '', freightType: '', coverOption: '', insuredItem: '', sumInsured: '', coverPeriod: '', commencementDate: '', expiryDate: '', description: '', clientOID: '' });
    setInsuredItems([]);
    setClientSearch('');
    setClientSuggestions([]);
    setSelectedClient(null);
    setShowNewClientForm(false);
    setNewClient({ identificationNo: '', firstName: '', otherNames: '', surname: '', pin: '', mobile: '', email: '' });
    setAttachedFiles([]);
  };

  const clientDisplayName = (c) =>
    [c.FirstName, c.OtherNames, c.Surname].filter(Boolean).join(' ').trim();

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setProposalForm((prev) => ({ ...prev, clientOID: client.OID }));
    setClientSearch(clientDisplayName(client));
    setClientSuggestions([]);
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    setProposalForm((prev) => ({ ...prev, clientOID: '' }));
    setClientSearch('');
    setClientSuggestions([]);
  };

  const handleCreateClient = async () => {
    if (!newClient.identificationNo || !newClient.firstName || !newClient.surname) {
      showToast('Please fill in identification, first name and surname');
      return;
    }
    setSavingClient(true);
    try {
      const response = await fetch('https://uat.definiteassurance.com/api/marine/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newClient, dealerPin: user?.pin })
      });
      const data = await response.json();
      if (data.success) {
        handleSelectClient(data.client);
        setShowNewClientForm(false);
        setNewClient({ identificationNo: '', firstName: '', otherNames: '', surname: '', pin: '', mobile: '', email: '' });
        showToast('Client created successfully', 'success');
      } else {
        showToast(data.message || 'Error creating client');
      }
    } catch (error) {
      console.error('Error creating client:', error);
      showToast('Error creating client. Please try again.');
    } finally {
      setSavingClient(false);
    }
  };

  // Compute the premium breakdown from the selected item's PremiumRate / ValueType
  const computePremium = () => {
    const item = insuredItems.find((i) => String(i.OID) === String(proposalForm.insuredItem));
    if (!item) return null;

    const sumInsured = parseFloat(proposalForm.sumInsured) || 0;
    const premiumRate = parseFloat(item.PremiumRate) || 0;
    const valueType = Number(item.ValueType);

    // ValueType 1 => PremiumRate is a percentage; ValueType 2 => flat amount
    const basicPremium = valueType === 1 ? sumInsured * (premiumRate / 100) : premiumRate;

    // Stamp duty: 40 flat for Airfreight; KShs 5 per (each) KShs 10,000 of sum insured for Seafreight
    const stampDuty = proposalForm.freightType === 'Airfreight'
      ? 40
      : Math.ceil(sumInsured / 10000) * 5;

    const levies = stampDuty;
    const grossPremium = basicPremium + levies;
    const commission = grossPremium * 0.175; // 17.5% of gross premium (basic + levies)

    return { basicPremium, stampDuty, levies, grossPremium, commission };
  };

  const handleCalculatePremium = (e) => {
    e.preventDefault();
    const breakdown = computePremium();
    if (!breakdown) {
      showToast('Unable to calculate premium. Please check the selected item.');
      return;
    }
    setPremiumBreakdown(breakdown);
    setProposalStep(2);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachedFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removeAttachedFile = (idx) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const uploadProposalDocuments = async (proposalOID) => {
    if (!attachedFiles.length || !proposalOID || !user?.oid) return;
    try {
      const formData = new FormData();
      attachedFiles.forEach((file) => formData.append('documents', file));
      const response = await fetch(
        `https://uat.definiteassurance.com/api/marine/uploadDocuments?agentCode=${encodeURIComponent(user.oid)}&proposalOID=${encodeURIComponent(proposalOID)}`,
        { method: 'POST', body: formData }
      );
      const data = await response.json();
      if (!data.success) {
        showToast('Proposal saved, but documents failed to upload');
      }
    } catch (error) {
      console.error('Error uploading documents:', error);
      showToast('Proposal saved, but documents failed to upload');
    }
  };

  // Format a value as an SQL datetime string: 2026-07-04 12:38:00.000
  const toSqlDateTime = (value) => {
    const d = value ? new Date(value) : new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.000`;
  };

  const handleFinalSubmit = async () => {
    if (!proposalForm.clientOID) {
      showToast('Please select or create a client');
      return;
    }
    if (!premiumBreakdown) {
      showToast('Premium has not been calculated');
      return;
    }

    const { grossPremium, basicPremium, commission, stampDuty } = premiumBreakdown;

    const body = {
      commissionRate: 17.5,
      agentCode: user?.oid,
      agentName: user?.name,
      prdCode: 'PRD-061',
      client: selectedClient?.IdentificationNo,
      commencementUnix: `${proposalForm.commencementDate} 00:00:00.000`,
      expiryUnix: `${proposalForm.expiryDate} 00:00:00.000`,
      today: toSqlDateTime(),
      coverDays: 60,
      coverOptioncode: proposalForm.coverOption,
      productCode: 3609,
      premium: grossPremium,
      basicPremium: basicPremium,
      commission: commission,
      stampduty: stampDuty,
      PHCF: 0,
      trainingLevy: 0,
      netCommission: commission * 0.9,
      netPremium: grossPremium - commission,
      selectedClient: proposalForm.clientOID,
      selectedItem: proposalForm.insuredItem,
      branch: user?.branch,
      sumInsured: parseFloat(proposalForm.sumInsured) || 0
    };

    setSubmittingProposal(true);
    try {
      if (editingProposalOID) {
        // Edit mode — update the existing proposal
        const response = await fetch('https://uat.definiteassurance.com/api/marine/editProposal', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...body, proposalOID: editingProposalOID })
        });
        const data = await response.json();

        if (data.success) {
          await uploadProposalDocuments(editingProposalOID);
          showToast('Proposal updated successfully', 'success');
          setProposalsRefresh((v) => v + 1);
          closeProposalForm();
        } else {
          showToast(data.message || 'Failed to update proposal');
        }
      } else {
        // Create mode
        const response = await fetch('https://uat.definiteassurance.com/api/marine/createProposal', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await response.json();

        if (data.success) {
          setProposalResult(data);
          setProposalPayload(body);
          await uploadProposalDocuments(data.proposalOID);
          showToast('Proposal created successfully', 'success');
          setProposalStep(4);
        } else {
          showToast(data.message || 'Failed to create proposal');
        }
      }
    } catch (error) {
      console.error('Error saving proposal:', error);
      showToast('Error saving proposal. Please try again.');
    } finally {
      setSubmittingProposal(false);
    }
  };

  const handleDownloadDebitNote = async () => {
    try {
      const coverLabel = availableCoverOptions.find((o) => o.value === proposalForm.coverOption)?.label || proposalForm.coverOption;
      const item = insuredItems.find((i) => String(i.OID) === String(proposalForm.insuredItem));
      const clientName = selectedClient ? clientDisplayName(selectedClient) : '';

      await generateDebitNote({
        date: new Date().toLocaleDateString(),
        debitNoteNo: proposalResult?.docNo,
        proposalNo: `PR-${proposalResult?.proposalOID}-${new Date().getFullYear()}`,
        client: {
          name: clientName,
          id: selectedClient?.IdentificationNo
        },
        agent: {
          name: user?.name,
          code: user?.oid,
          branch: user?.branch
        },
        cover: {
          cargoType: proposalForm.cargoType,
          freightType: proposalForm.freightType,
          coverOption: coverLabel,
          item: item?.ItemName,
          sumInsured: proposalForm.sumInsured,
          commencement: proposalForm.commencementDate,
          expiry: proposalForm.expiryDate,
          coverDays: 60
        },
        premium: premiumBreakdown
          ? {
              basicPremium: premiumBreakdown.basicPremium,
              stampDuty: premiumBreakdown.stampDuty,
              PHCF: 0,
              trainingLevy: 0,
              commission: premiumBreakdown.commission,
              netPremium: premiumBreakdown.grossPremium - premiumBreakdown.commission,
              grossPremium: premiumBreakdown.grossPremium
            }
          : {}
      });
    } catch (error) {
      console.error('Error generating debit note:', error);
      showToast('Unable to generate debit note');
    }
  };

  // Generate an M-Pesa-style unique transaction reference (10 alphanumeric chars)
  const generateTransactionRef = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let ref = '';
    for (let i = 0; i < 10; i++) ref += chars[Math.floor(Math.random() * chars.length)];
    return ref;
  };

  const handleProceedToPayment = async () => {
    if (!proposalPayload || !proposalResult) {
      showToast('Proposal details are missing. Please try again.');
      return;
    }

    const body = {
      ...proposalPayload,
      noteOID: proposalResult.noteOID,
      docNo: proposalResult.docNo,
      proposalOID: proposalResult.proposalOID,
      transaction: generateTransactionRef()
    };

    setPayingProposal(true);
    try {
      const response = await fetch('https://uat.definiteassurance.com/api/marine/approveProposal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (data.success) {
        showToast('Payment successful. Proposal approved.', 'success');
        setProposalsRefresh((v) => v + 1);
        closeProposalForm();
      } else {
        showToast(data.message || 'Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      showToast('Error processing payment. Please try again.');
    } finally {
      setPayingProposal(false);
    }
  };

  // Map a cover option value back to its label (across both cargo types)
  const coverLabelFromValue = (value) => {
    const all = [
      ...coverOptionsByCargoType.Containerized,
      ...coverOptionsByCargoType['Non-Containerized']
    ];
    return all.find((o) => String(o.value) === String(value))?.label || value;
  };

  // --- Actions for a proposal in the New Proposals list ---
  const handleDownloadDebitNoteForProposal = async (p) => {
    try {
      const clientName = [p.FirstName, p.OtherNames, p.Surname].filter(Boolean).join(' ').trim();
      await generateDebitNote({
        date: new Date().toLocaleDateString(),
        debitNoteNo: p.DocNo,
        proposalNo: `PR-${p.OID}-${new Date().getFullYear()}`,
        client: { name: clientName, id: p.IDNO },
        agent: { name: user?.name, code: user?.oid, branch: user?.branch },
        cover: {
          cargoType: '-',
          freightType: '-',
          coverOption: coverLabelFromValue(p.CoverOption),
          item: p.ItemName,
          sumInsured: p.SumInsured,
          commencement: formatDate(p.CommencementDate),
          expiry: formatDate(p.ExpiryDate),
          coverDays: p.PeriodOfCover
        },
        premium: {
          basicPremium: p.BasicPremium,
          stampDuty: p.StampDuty,
          PHCF: p.PHCF,
          trainingLevy: p.TrainingLevy,
          commission: p.GrossCommission,
          netPremium: p.NetPremium,
          grossPremium: p.GrossPremium
        }
      });
    } catch (error) {
      console.error('Error generating debit note:', error);
      showToast('Unable to generate debit note');
    }
  };

  const handleViewPolicyDetails = async (p) => {
    setShowPolicyDetails(true);
    setPolicyDetails(null);
    setLoadingPolicyDetails(true);
    try {
      const response = await fetch(`https://uat.definiteassurance.com/api/marine/policy-details?oid=${encodeURIComponent(p.OID)}`);
      const data = await response.json();
      if (data.success) {
        setPolicyDetails(data.policy);
      } else {
        showToast(data.message || 'Unable to load policy details');
        setShowPolicyDetails(false);
      }
    } catch (error) {
      console.error('Error fetching policy details:', error);
      showToast('Error loading policy details');
      setShowPolicyDetails(false);
    } finally {
      setLoadingPolicyDetails(false);
    }
  };

  const handleDownloadPolicyDocument = async (p) => {
    try {
      const clientName = [p.FirstName, p.OtherNames, p.Surname].filter(Boolean).join(' ').trim();
      await generateDebitNote({
        docTitle: 'POLICY DOCUMENT',
        refLabel: 'Policy No',
        filePrefix: 'PolicyDocument',
        showPayment: false,
        date: new Date().toLocaleDateString(),
        debitNoteNo: p.PolicyNo,
        proposalNo: `PR-${p.OID}-${new Date().getFullYear()}`,
        client: { name: clientName, id: p.IDNO },
        agent: { name: user?.name, code: user?.oid, branch: user?.branch },
        cover: {
          cargoType: '-',
          freightType: '-',
          coverOption: coverLabelFromValue(p.CoverOption),
          item: p.ItemName,
          sumInsured: p.SumInsured,
          commencement: formatDate(p.CommencementDate),
          expiry: formatDate(p.ExpiryDate),
          coverDays: p.PeriodOfCover
        },
        premium: {
          basicPremium: p.BasicPremium,
          stampDuty: p.StampDuty,
          PHCF: p.PHCF,
          trainingLevy: p.TrainingLevy,
          commission: p.GrossCommission,
          netPremium: p.NetPremium,
          grossPremium: p.GrossPremium
        }
      });
    } catch (error) {
      console.error('Error generating policy document:', error);
      showToast('Unable to generate policy document');
    }
  };

  const handleProceedToPaymentForProposal = async (p) => {
    const body = {
      commissionRate: p.CommissionRate ?? 17.5,
      agentCode: user?.oid,
      agentName: user?.name,
      prdCode: 'PRD-061',
      client: p.IDNO,
      commencementUnix: `${formatDate(p.CommencementDate)} 00:00:00.000`,
      expiryUnix: `${formatDate(p.ExpiryDate)} 00:00:00.000`,
      today: toSqlDateTime(),
      coverDays: p.PeriodOfCover,
      coverOptioncode: p.CoverOption,
      productCode: 3609,
      premium: p.GrossPremium,
      basicPremium: p.BasicPremium,
      commission: p.GrossCommission,
      stampduty: p.StampDuty,
      PHCF: p.PHCF,
      trainingLevy: p.TrainingLevy,
      netCommission: p.NetCommission,
      netPremium: p.NetPremium,
      selectedClient: p.Client,
      selectedItem: p.ProductInsuredItem,
      branch: user?.branch,
      sumInsured: p.SumInsured,
      noteOID: p.NoteOID,
      docNo: p.DocNo,
      proposalOID: p.OID,
      transaction: generateTransactionRef()
    };

    setPayingProposalId(p.OID);
    try {
      const response = await fetch('https://uat.definiteassurance.com/api/marine/approveProposal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (data.success) {
        showToast('Payment successful. Proposal approved.', 'success');
        setProposalsRefresh((v) => v + 1);
      } else {
        showToast(data.message || 'Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      showToast('Error processing payment. Please try again.');
    } finally {
      setPayingProposalId(null);
    }
  };

  // Infer cargo type from a stored cover option value (ICC AIR 3358 is ambiguous -> default Containerized)
  const inferCargoType = (coverValue) => {
    const inC = coverOptionsByCargoType.Containerized.some((o) => String(o.value) === String(coverValue));
    const inN = coverOptionsByCargoType['Non-Containerized'].some((o) => String(o.value) === String(coverValue));
    if (inN && !inC) return 'Non-Containerized';
    return 'Containerized';
  };

  // Cover period isn't stored on the proposal — estimate it from the stored date range
  const inferCoverPeriod = (commencementDate, expiryDate) => {
    if (!commencementDate || !expiryDate) return '';
    const diffDays = Math.round((new Date(expiryDate) - new Date(commencementDate)) / (1000 * 60 * 60 * 24));
    if (diffDays <= 10) return '1Week';
    if (diffDays <= 45) return '1Month';
    if (diffDays <= 100) return '3Months';
    return '6Months';
  };

  const handleEditProposal = (p) => {
    // Freight type isn't stored — infer from stamp duty (40 flat = Airfreight)
    const freightType = Number(p.StampDuty) === 40 ? 'Airfreight' : 'Seafreight';
    const cargoType = inferCargoType(p.CoverOption);
    const commencementDate = formatDate(p.CommencementDate) !== '-' ? formatDate(p.CommencementDate) : '';
    const expiryDate = formatDate(p.ExpiryDate) !== '-' ? formatDate(p.ExpiryDate) : '';

    setEditingProposalOID(p.OID);
    setProposalForm({
      cargoType,
      freightType,
      coverOption: String(p.CoverOption ?? ''),
      insuredItem: String(p.ProductInsuredItem ?? ''),
      sumInsured: p.SumInsured != null ? String(p.SumInsured) : '',
      coverPeriod: inferCoverPeriod(commencementDate, expiryDate),
      commencementDate,
      expiryDate,
      description: '',
      clientOID: p.Client
    });

    // Prefill the selected client (from the proposal row)
    setSelectedClient({
      OID: p.Client,
      FirstName: p.FirstName,
      OtherNames: p.OtherNames,
      Surname: p.Surname,
      IdentificationNo: p.IDNO
    });
    setClientSearch([p.FirstName, p.OtherNames, p.Surname].filter(Boolean).join(' ').trim());

    setPremiumBreakdown(null);
    setProposalStep(1);
    setShowProposalForm(true);
  };

  const formatMoney = (value) =>
    (Number(value) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '-';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // Format a numeric string with thousands separators for display
  const formatWithCommas = (value) => {
    if (value === '' || value == null) return '';
    const parts = value.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const handleSumInsuredChange = (e) => {
    // Keep only digits and a single decimal point in state; display is comma-formatted
    const raw = e.target.value.replace(/,/g, '');
    if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
      setProposalForm({ ...proposalForm, sumInsured: raw });
    }
  };

  const coverPeriodOptions = [
    { value: '1Week', label: '1 Week' },
    { value: '1Month', label: '1 Month' },
    { value: '3Months', label: '3 Months' },
    { value: '6Months', label: '6 Months' },
  ];

  // Expiry date is derived from the commencement date plus the selected cover period
  const calculateExpiryDate = (commencementDate, coverPeriod) => {
    if (!commencementDate || !coverPeriod) return '';
    const d = new Date(commencementDate);
    switch (coverPeriod) {
      case '1Week':
        d.setDate(d.getDate() + 7);
        break;
      case '1Month':
        d.setMonth(d.getMonth() + 1);
        break;
      case '3Months':
        d.setMonth(d.getMonth() + 3);
        break;
      case '6Months':
        d.setMonth(d.getMonth() + 6);
        break;
      default:
        return '';
    }
    return d.toISOString().split('T')[0];
  };

  const handleCoverPeriodChange = (e) => {
    const coverPeriod = e.target.value;
    const expiryDate = calculateExpiryDate(proposalForm.commencementDate, coverPeriod);
    setProposalForm({ ...proposalForm, coverPeriod, expiryDate });
  };

  const handleCommencementDateChange = (e) => {
    const commencementDate = e.target.value;
    const expiryDate = calculateExpiryDate(commencementDate, proposalForm.coverPeriod);
    setProposalForm({ ...proposalForm, commencementDate, expiryDate });
  };

  const availableCoverOptions = proposalForm.cargoType
    ? coverOptionsByCargoType[proposalForm.cargoType]
    : [];

  // Filter a proposals/policies list by search term (client name / OID / policy no.) and DateDone range
  const filterProposals = (list, search, from, to) =>
    list.filter((p) => {
      const term = search.trim().toLowerCase();
      if (term) {
        const name = [p.FirstName, p.OtherNames, p.Surname].filter(Boolean).join(' ').toLowerCase();
        const matches =
          name.includes(term) ||
          String(p.OID).toLowerCase().includes(term) ||
          String(p.PolicyNo || '').toLowerCase().includes(term);
        if (!matches) return false;
      }
      if (from || to) {
        if (!p.DateDone) return false;
        const done = new Date(p.DateDone);
        if (from && done < new Date(`${from}T00:00:00`)) return false;
        if (to && done > new Date(`${to}T23:59:59`)) return false;
      }
      return true;
    });

  const filteredProposals = filterProposals(proposalsList, proposalSearch, dateFrom, dateTo);
  const filteredPolicies = filterProposals(policiesList, policySearch, policyDateFrom, policyDateTo);

  // When a cover option is selected, fetch matching items from ProductInsuredSchedule
  useEffect(() => {
    if (!proposalForm.coverOption) {
      setInsuredItems([]);
      return;
    }

    let cancelled = false;
    setLoadingItems(true);

    fetch(`https://uat.definiteassurance.com/api/marine/insured-schedule?coverOption=${encodeURIComponent(proposalForm.coverOption)}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setInsuredItems(data.success ? data.items : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching insured schedule:', err);
        setInsuredItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingItems(false);
      });

    return () => { cancelled = true; };
  }, [proposalForm.coverOption]);

  // Debounced client search (by IdentificationNo or FirstName + OtherNames)
  useEffect(() => {
    if (selectedClient) return;
    const term = clientSearch.trim();
    if (term.length < 2) {
      setClientSuggestions([]);
      return;
    }

    let cancelled = false;
    setSearchingClients(true);
    const timer = setTimeout(() => {
      fetch(`https://uat.definiteassurance.com/api/marine/clients/search?q=${encodeURIComponent(term)}`)
        .then((res) => res.json())
        .then((data) => {
          if (cancelled) return;
          setClientSuggestions(data.success ? data.clients : []);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error('Error searching clients:', err);
          setClientSuggestions([]);
        })
        .finally(() => {
          if (!cancelled) setSearchingClients(false);
        });
    }, 300);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [clientSearch, selectedClient]);

  // Fetch the agent's proposals when the New Proposals tab is active
  useEffect(() => {
    if (activeTab !== 'new-proposals' || !user?.oid) return;
    let cancelled = false;
    setLoadingProposals(true);
    fetch(`https://uat.definiteassurance.com/api/marine/proposals?agentCode=${encodeURIComponent(user.oid)}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setProposalsList(data.success ? data.proposals : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching proposals:', err);
        setProposalsList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingProposals(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, proposalsRefresh, user?.oid]);

  // Fetch the agent's active policies (Status=1) when the Active Policies tab is active
  useEffect(() => {
    if (activeTab !== 'active-policies' || !user?.oid) return;
    let cancelled = false;
    setLoadingPolicies(true);
    fetch(`https://uat.definiteassurance.com/api/marine/proposals?agentCode=${encodeURIComponent(user.oid)}&status=1`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setPoliciesList(data.success ? data.proposals : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching policies:', err);
        setPoliciesList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPolicies(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, user?.oid]);

  // Fetch the agent's document folders when the Documents tab is active
  useEffect(() => {
    if (activeTab !== 'documents' || !user?.oid) return;
    setOpenFolder(null);
    setFolderFiles([]);
    let cancelled = false;
    setLoadingFolders(true);
    fetch(`https://uat.definiteassurance.com/api/marine/documents?agentCode=${encodeURIComponent(user.oid)}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setDocumentFolders(data.success ? data.folders : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching document folders:', err);
        setDocumentFolders([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingFolders(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, user?.oid]);

  // Fetch report data (active policies) when the Reports tab is active
  useEffect(() => {
    if (activeTab !== 'reports' || !user?.oid) return;
    let cancelled = false;
    setLoadingReport(true);
    fetch(`https://uat.definiteassurance.com/api/marine/proposals?agentCode=${encodeURIComponent(user.oid)}&status=1`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setReportData(data.success ? data.proposals : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching report data:', err);
        setReportData([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingReport(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, user?.oid]);

  // Fetch the agent profile + personal documents when the My Profile tab is active
  useEffect(() => {
    if (activeTab !== 'profile' || !user?.oid) return;
    let cancelled = false;
    setLoadingProfile(true);
    setEditingProfile(false);

    Promise.all([
      fetch(`https://uat.definiteassurance.com/api/marine/agent-profile?oid=${encodeURIComponent(user.oid)}`).then((r) => r.json()),
      fetch(`https://uat.definiteassurance.com/api/marine/profile/documents?agentCode=${encodeURIComponent(user.oid)}`).then((r) => r.json())
    ])
      .then(([profileRes, docsRes]) => {
        if (cancelled) return;
        setProfile(profileRes.success ? profileRes.profile : null);
        setProfileDocs(docsRes.success ? docsRes.files : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading profile:', err);
        setProfile(null);
        setProfileDocs([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingProfile(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, user?.oid, profileRefresh]);

  // Fetch overview analytics when the Overview tab is active or month/year changes
  useEffect(() => {
    if (activeTab !== 'overview' || !user?.oid) return;
    let cancelled = false;
    setLoadingOverview(true);
    fetch(`https://uat.definiteassurance.com/api/marine/overview?agentCode=${encodeURIComponent(user.oid)}&year=${overviewYear}&month=${overviewMonth}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setOverview(data.success ? data : null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching overview:', err);
        setOverview(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingOverview(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, user?.oid, overviewYear, overviewMonth]);

  // Apply and persist theme / notification preferences
  useEffect(() => {
    document.body.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => { localStorage.setItem('emailAlerts', String(emailAlerts)); }, [emailAlerts]);
  useEffect(() => { localStorage.setItem('smsAlerts', String(smsAlerts)); }, [smsAlerts]);

  const monthNames =['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const renderOverview = () => {
    const currentYear = new Date().getFullYear();
    const years = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];
    const agencyName = overview?.agency?.Dealer_name || overview?.agency?.DealerFullName || user?.name || 'Agency';
    const approved = Number(overview?.agency?.Approved) === 1;
    const production = overview?.production || [];
    const st = overview?.stats || { activePolicies: 0, remittedPremiums: 0, commission: 0, pendingApproval: 0 };

    const maxPremium = Math.max(1, ...production.map((p) => Number(p.premium) || 0));
    const totalPolicies = production.reduce((a, p) => a + (Number(p.policies) || 0), 0);
    const totalPending = production.reduce((a, p) => a + (Number(p.pending) || 0), 0);
    const statusTotal = totalPolicies + totalPending;
    const activePct = statusTotal ? Math.round((totalPolicies / statusTotal) * 100) : 0;

    const statCards = [
      { label: 'Active Policies', value: st.activePolicies, icon: '⚓', color: '#1a5c5a' },
      { label: 'Remitted Premiums', value: `KES ${formatMoney(st.remittedPremiums)}`, icon: '💰', color: '#FF4444' },
      { label: 'Commission', value: `KES ${formatMoney(st.commission)}`, icon: '📈', color: '#1a5c5a' },
      { label: 'Pending Approval', value: st.pendingApproval, icon: '⏳', color: '#FF4444' }
    ];

    return (
      <div className="overview-section">
        <div className="welcome-banner">
          <div className="welcome-text">
            <div className="welcome-top">
              <h2>Welcome, {agencyName}</h2>
              <span className={`agency-badge ${approved ? 'approved' : 'unapproved'}`}>
                {approved ? '✓ Approved' : '✕ Unapproved'}
              </span>
            </div>
            <p className="welcome-date">{new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <p className="welcome-phrase">You are Definitely Covered.</p>
          </div>
          <img src="/definitelogo.png" alt="Definite" className="welcome-logo" />
        </div>

        <div className="overview-controls">
          <h3>Monthly Summary</h3>
          <div className="control-group">
            <select value={overviewMonth} onChange={(e) => setOverviewMonth(Number(e.target.value))}>
              {monthNames.map((mn, i) => <option key={i} value={i + 1}>{mn}</option>)}
            </select>
            <select value={overviewYear} onChange={(e) => setOverviewYear(Number(e.target.value))}>
              {years.map((yr) => <option key={yr} value={yr}>{yr}</option>)}
            </select>
          </div>
        </div>

        <div className="stats-grid">
          {statCards.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: stat.color }}>{stat.icon}</div>
              <div className="stat-content">
                <p className="stat-label">{stat.label}</p>
                <p className="stat-value">{loadingOverview ? '…' : stat.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="dashboard-grid overview-analytics">
          <div className="card chart-card">
            <div className="chart-header">
              <h3>Production Overview — {overviewYear}</h3>
              <select value={overviewYear} onChange={(e) => setOverviewYear(Number(e.target.value))}>
                {years.map((yr) => <option key={yr} value={yr}>{yr}</option>)}
              </select>
            </div>
            <div className="bar-chart">
              {production.map((mo) => (
                <div className="bar-col" key={mo.month}>
                  <div className="bar-track">
                    <div
                      className="bar-fill"
                      style={{ height: `${(Number(mo.premium) / maxPremium) * 100}%` }}
                      title={`${monthNames[mo.month - 1]}: KES ${formatMoney(mo.premium)} · ${mo.policies} policies`}
                    ></div>
                  </div>
                  <span className="bar-label">{monthShort[mo.month - 1]}</span>
                </div>
              ))}
            </div>
            <p className="chart-caption">Gross premium remitted per month (KES)</p>
          </div>

          <div className="card">
            <h3>Policy Status ({overviewYear})</h3>
            <div className="donut-wrap">
              <div
                className="donut"
                style={{ background: `conic-gradient(var(--primary-teal) 0 ${activePct}%, var(--primary-red) ${activePct}% 100%)` }}
              >
                <div className="donut-hole">
                  <span className="donut-total">{statusTotal}</span>
                  <span className="donut-sub">Total</span>
                </div>
              </div>
              <div className="donut-legend">
                <div><span className="dot teal"></span> Active Policies <strong>{totalPolicies}</strong></div>
                <div><span className="dot red"></span> Pending Approval <strong>{totalPending}</strong></div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <h3>Recently Booked Covers</h3>
          {(overview?.recent || []).length === 0 ? (
            <p className="field-hint">No covers booked yet.</p>
          ) : (
            <div className="report-table-wrap">
              <table className="policies-table">
                <thead>
                  <tr>
                    <th>Policy No</th>
                    <th>Client</th>
                    <th>Item</th>
                    <th>Sum Insured</th>
                    <th>Premium</th>
                    <th>Date Booked</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview?.recent || []).map((r) => (
                    <tr key={r.OID}>
                      <td>{r.PolicyNo || '-'}</td>
                      <td>{[r.FirstName, r.OtherNames, r.Surname].filter(Boolean).join(' ') || '-'}</td>
                      <td>{r.ItemName || '-'}</td>
                      <td>KES {formatMoney(r.SumInsured)}</td>
                      <td>KES {formatMoney(r.GrossPremium)}</td>
                      <td>{formatDate(r.DateDone)}</td>
                      <td>
                        <span className={`status-badge ${Number(r.Status) === 1 ? 'active' : 'in-port'}`}>
                          {Number(r.Status) === 1 ? 'Active' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const docFileUrl = (proposalOID, name, download) =>
    `https://uat.definiteassurance.com/api/marine/documents/file?agentCode=${encodeURIComponent(user?.oid)}&proposalOID=${encodeURIComponent(proposalOID)}&name=${encodeURIComponent(name)}${download ? '&download=1' : ''}`;

  const handleViewFile = (proposalOID, name) => {
    window.open(docFileUrl(proposalOID, name, false), '_blank', 'noopener');
  };

  const handleDownloadFile = (proposalOID, name) => {
    const a = document.createElement('a');
    a.href = docFileUrl(proposalOID, name, true);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleOpenFolder = async (proposalOID) => {
    setOpenFolder(proposalOID);
    setFolderFiles([]);
    setLoadingFiles(true);
    try {
      const response = await fetch(`https://uat.definiteassurance.com/api/marine/documents/files?agentCode=${encodeURIComponent(user.oid)}&proposalOID=${encodeURIComponent(proposalOID)}`);
      const data = await response.json();
      setFolderFiles(data.success ? data.files : []);
    } catch (error) {
      console.error('Error fetching folder files:', error);
      setFolderFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const renderDocuments = () => {
    const currentFolder = documentFolders.find((f) => String(f.proposalOID) === String(openFolder));
    const term = folderSearch.trim().toLowerCase();
    const visibleFolders = documentFolders.filter((f) =>
      !term ||
      String(f.proposalOID).toLowerCase().includes(term) ||
      String(f.clientName || '').toLowerCase().includes(term)
    );

    return (
      <div className="documents-section">
        <div className="section-header">
          <h2>Documents</h2>
        </div>

        {!openFolder ? (
          <>
            <div className="proposals-toolbar">
              <input
                type="text"
                className="proposals-search"
                value={folderSearch}
                onChange={(e) => setFolderSearch(e.target.value)}
                placeholder="Search folders by proposal OID or client name"
              />
              {folderSearch && (
                <div className="date-filter">
                  <button className="btn-secondary btn-small" onClick={() => setFolderSearch('')}>Clear</button>
                </div>
              )}
            </div>
            <div className="card">
              {loadingFolders ? (
                <p className="field-hint">Loading folders...</p>
              ) : documentFolders.length === 0 ? (
                <p className="field-hint">No document folders found yet.</p>
              ) : visibleFolders.length === 0 ? (
                <p className="field-hint">No folders match your search.</p>
              ) : (
                <div className="folder-grid">
                  {visibleFolders.map((folder) => (
                    <button
                      key={folder.proposalOID}
                      className="folder-card"
                      onClick={() => handleOpenFolder(folder.proposalOID)}
                    >
                      <span className="folder-icon">📁</span>
                      <span className="folder-name">PR-{folder.proposalOID}</span>
                      <span className="folder-client">{folder.clientName || 'Unknown client'}</span>
                      <span className="folder-count">{folder.fileCount} file{folder.fileCount === 1 ? '' : 's'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="card">
            <div className="folder-breadcrumb">
              <button className="btn-secondary btn-small" onClick={() => { setOpenFolder(null); setFolderFiles([]); }}>
                ← Back to folders
              </button>
              <span className="breadcrumb-current">
                PR-{openFolder}{currentFolder?.clientName ? ` · ${currentFolder.clientName}` : ''}
              </span>
            </div>

            {loadingFiles ? (
              <p className="field-hint">Loading files...</p>
            ) : folderFiles.length === 0 ? (
              <p className="field-hint">This folder has no files.</p>
            ) : (
              <div className="doc-file-list">
                {folderFiles.map((file) => (
                  <div key={file.name} className="doc-file-item">
                    <span className="doc-file-name">📄 {file.name}</span>
                    <div className="doc-file-actions">
                      <button className="btn-small" onClick={() => handleViewFile(openFolder, file.name)}>View</button>
                      <button className="btn-small btn-small-primary" onClick={() => handleDownloadFile(openFolder, file.name)}>Download</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderNewProposals = () => (
    <div className="proposals-section">
      <div className="section-header">
        <h2>New Proposals</h2>
        <button className="btn-primary" onClick={handleCreateProposalClick} disabled={checkingApproval}>
          {checkingApproval ? 'Checking...' : '+ Create Proposal'}
        </button>
      </div>
      <div className="proposals-toolbar">
        <input
          type="text"
          className="proposals-search"
          value={proposalSearch}
          onChange={(e) => setProposalSearch(e.target.value)}
          placeholder="Search by client name, proposal OID or policy no."
        />
        <div className="date-filter">
          <label>From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <label>To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          {(proposalSearch || dateFrom || dateTo) && (
            <button
              className="btn-secondary btn-small"
              onClick={() => { setProposalSearch(''); setDateFrom(''); setDateTo(''); }}
            >
              Clear
            </button>
          )}
        </div>
      </div>
      <div className="card">
        {loadingProposals ? (
          <p className="field-hint">Loading proposals...</p>
        ) : proposalsList.length === 0 ? (
          <p className="field-hint">No proposals found yet. Click "+ Create Proposal" to get started.</p>
        ) : filteredProposals.length === 0 ? (
          <p className="field-hint">No proposals match your search/filter.</p>
        ) : (
          <div className="proposals-list">
            {filteredProposals.map((p) => (
              <div key={p.OID} className="proposal-item">
                <div className="proposal-header">
                  <h4>{p.ProductDescription}{p.ItemName ? ` — ${p.ItemName}` : ''}</h4>
                  <span className="proposal-status pending">Pending</span>
                </div>
                <p className="proposal-description">
                  Policy: {p.PolicyNo || '-'} · Proposal: {p.ProposalNo || '-'} · OID: {p.OID}
                </p>
                <div className="proposal-details">
                  <span>📅 {formatDate(p.CommencementDate)} → {formatDate(p.ExpiryDate)}</span>
                  <span>🗓 {p.PeriodOfCover || 0} days</span>
                  <span>🕒 Date Done: {formatDate(p.DateDone)}</span>
                  <span>💰 Sum Insured: KES {formatMoney(p.SumInsured)}</span>
                  <span>Premium: KES {formatMoney(p.GrossPremium)}</span>
                </div>
                <div className="proposal-actions">
                  <button className="btn-small" onClick={() => handleDownloadDebitNoteForProposal(p)}>
                    Download Debit Note
                  </button>
                  <button
                    className="btn-small btn-small-primary"
                    onClick={() => handleProceedToPaymentForProposal(p)}
                    disabled={payingProposalId === p.OID}
                  >
                    {payingProposalId === p.OID ? 'Processing...' : 'Proceed to Payment'}
                  </button>
                  <button className="btn-small" onClick={() => handleEditProposal(p)}>
                    Edit Proposal
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showProposalForm && (
        <div className="modal-overlay" onClick={closeProposalForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <h3>{editingProposalOID ? 'Edit Proposal' : 'Create New Proposal'}</h3>
                <span className="step-indicator">
                  Step {proposalStep} of {editingProposalOID ? 3 : 4} — {proposalStep === 1 ? 'Proposal Details' : proposalStep === 2 ? 'Premium Breakdown' : proposalStep === 3 ? 'Client Details' : 'Complete'}
                </span>
              </div>
              <button className="modal-close" onClick={closeProposalForm}>×</button>
            </div>

            {proposalStep === 1 && (
            <form onSubmit={handleCalculatePremium}>
              <div className="form-group full-width">
                <label>Packaging Type</label>
                <div className="radio-group">
                  <label className={`radio-option ${proposalForm.cargoType === 'Containerized' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="cargoType"
                      value="Containerized"
                      checked={proposalForm.cargoType === 'Containerized'}
                      onChange={(e) => handleProposalTypeChange(e.target.value)}
                    />
                    <span>Containerized</span>
                  </label>
                  <label className={`radio-option ${proposalForm.cargoType === 'Non-Containerized' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="cargoType"
                      value="Non-Containerized"
                      checked={proposalForm.cargoType === 'Non-Containerized'}
                      onChange={(e) => handleProposalTypeChange(e.target.value)}
                    />
                    <span>Non-Containerized</span>
                  </label>
                </div>
              </div>

              <div className="form-group full-width">
                <label>Shipping Mode</label>
                <div className="radio-group">
                  <label className={`radio-option ${proposalForm.freightType === 'Airfreight' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="freightType"
                      value="Airfreight"
                      checked={proposalForm.freightType === 'Airfreight'}
                      onChange={(e) => setProposalForm({ ...proposalForm, freightType: e.target.value })}
                    />
                    <span>Airfreight</span>
                  </label>
                  <label className={`radio-option ${proposalForm.freightType === 'Seafreight' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="freightType"
                      value="Seafreight"
                      checked={proposalForm.freightType === 'Seafreight'}
                      onChange={(e) => setProposalForm({ ...proposalForm, freightType: e.target.value })}
                    />
                    <span>Seafreight</span>
                  </label>
                </div>
              </div>

              {proposalForm.cargoType && (
                <div className="form-group">
                  <label htmlFor="coverOption">Product Cover Options</label>
                  <select
                    id="coverOption"
                    value={proposalForm.coverOption}
                    onChange={(e) => setProposalForm({ ...proposalForm, coverOption: e.target.value, insuredItem: '', sumInsured: '', coverPeriod: '', commencementDate: '', expiryDate: '', description: '' })}
                    required
                  >
                    <option value="">Select a cover option</option>
                    {availableCoverOptions.map((opt, idx) => (
                      <option key={`${opt.value}-${idx}`} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {proposalForm.coverOption && (
                <div className="form-group">
                  <label htmlFor="insuredItem">Categories</label>
                  <select
                    id="insuredItem"
                    value={proposalForm.insuredItem}
                    onChange={(e) => setProposalForm({ ...proposalForm, insuredItem: e.target.value })}
                    disabled={loadingItems}
                    required
                  >
                    <option value="">
                      {loadingItems ? 'Loading items...' : 'Select an item'}
                    </option>
                    {insuredItems.map((item) => (
                      <option key={item.OID} value={item.OID}>
                        {item.ItemName}
                      </option>
                    ))}
                  </select>
                  {!loadingItems && insuredItems.length === 0 && (
                    <p className="field-hint">No items found for this cover option.</p>
                  )}
                </div>
              )}

              {proposalForm.insuredItem && (
                <>
                  <div className="form-group">
                    <label htmlFor="sumInsured">Sum Insured / Value of Consignment</label>
                    <div className="currency-input">
                      <span className="currency-prefix">KES</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        id="sumInsured"
                        value={formatWithCommas(proposalForm.sumInsured)}
                        onChange={handleSumInsuredChange}
                        placeholder="0.00"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="coverPeriod">Cover Period</label>
                    <select
                      id="coverPeriod"
                      value={proposalForm.coverPeriod}
                      onChange={handleCoverPeriodChange}
                      required
                    >
                      <option value="">Select cover period</option>
                      {coverPeriodOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="commencementDate">Commencement Date</label>
                    <input
                      type="date"
                      id="commencementDate"
                      value={proposalForm.commencementDate}
                      onChange={handleCommencementDateChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="expiryDate">Expiry Date</label>
                    <input
                      type="date"
                      id="expiryDate"
                      value={proposalForm.expiryDate}
                      disabled
                    />
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="description">Short Description</label>
                    <textarea
                      id="description"
                      rows="3"
                      value={proposalForm.description}
                      onChange={(e) => setProposalForm({ ...proposalForm, description: e.target.value })}
                      placeholder="Enter a short description"
                      required
                    />
                  </div>
                </>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeProposalForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={!proposalForm.cargoType || !proposalForm.freightType || !proposalForm.coverOption || !proposalForm.insuredItem || !proposalForm.sumInsured || !proposalForm.coverPeriod || !proposalForm.commencementDate || !proposalForm.description}>
                  Calculate Premium
                </button>
              </div>
            </form>
            )}

            {proposalStep === 2 && premiumBreakdown && (
              <div className="premium-step">
                <div className="premium-breakdown">
                  <div className="breakdown-row">
                    <span>Basic Premium</span>
                    <span>KES {formatMoney(premiumBreakdown.basicPremium)}</span>
                  </div>
                  <div className="breakdown-row">
                    <span>Levies (Stamp Duty — {proposalForm.freightType})</span>
                    <span>KES {formatMoney(premiumBreakdown.levies)}</span>
                  </div>
                  <div className="breakdown-row">
                    <span>Commission (17.5% of Gross)</span>
                    <span>KES {formatMoney(premiumBreakdown.commission)}</span>
                  </div>
                  <div className="breakdown-row total">
                    <span>Gross Premium (Basic + Levies)</span>
                    <span>KES {formatMoney(premiumBreakdown.grossPremium)}</span>
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setProposalStep(1)}>
                    ← Back
                  </button>
                  <button type="button" className="btn-primary" onClick={() => setProposalStep(3)}>
                    Continue
                  </button>
                </div>
              </div>
            )}

            {proposalStep === 3 && (
              <div className="premium-step">
                <div className="client-search">
                  <label htmlFor="clientSearch">Search Client (Identification or Name)</label>
                  <div className="client-search-input">
                    <input
                      type="text"
                      id="clientSearch"
                      value={clientSearch}
                      onChange={(e) => {
                        if (selectedClient) handleClearClient();
                        setClientSearch(e.target.value);
                      }}
                      placeholder="Type identification no. or name..."
                      autoComplete="off"
                    />
                    {selectedClient && (
                      <button type="button" className="client-clear" onClick={handleClearClient} title="Clear">×</button>
                    )}

                    {!selectedClient && clientSearch.trim().length >= 2 && (
                      <div className="client-suggestions">
                        {searchingClients && <div className="client-suggestion muted">Searching...</div>}
                        {!searchingClients && clientSuggestions.length === 0 && (
                          <div className="client-suggestion muted">No matching clients found.</div>
                        )}
                        {clientSuggestions.map((c) => (
                          <button
                            type="button"
                            key={c.OID}
                            className="client-suggestion"
                            onClick={() => handleSelectClient(c)}
                          >
                            <span className="client-name">{clientDisplayName(c)}</span>
                            <span className="client-id">ID: {c.IdentificationNo}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {selectedClient && (
                    <div className="selected-client">
                      <span className="selected-check">✓</span>
                      <div>
                        <p className="selected-client-name">{clientDisplayName(selectedClient)}</p>
                        <p className="selected-client-id">ID: {selectedClient.IdentificationNo} · OID: {selectedClient.OID}</p>
                      </div>
                    </div>
                  )}
                </div>

                {!showNewClientForm && !selectedClient && (
                  <button type="button" className="btn-secondary create-client-btn" onClick={() => setShowNewClientForm(true)}>
                    + Create New Client
                  </button>
                )}

                {showNewClientForm && (
                  <div className="new-client-form">
                    <div className="new-client-header">
                      <h4>New Client</h4>
                      <button type="button" className="modal-close" onClick={() => setShowNewClientForm(false)}>×</button>
                    </div>
                    <div className="new-client-grid">
                      <div className="form-group">
                        <label>Identification No.</label>
                        <input type="text" value={newClient.identificationNo} onChange={(e) => setNewClient({ ...newClient, identificationNo: e.target.value })} placeholder="ID / Passport / Reg No." />
                      </div>
                      <div className="form-group">
                        <label>First Name</label>
                        <input type="text" value={newClient.firstName} onChange={(e) => setNewClient({ ...newClient, firstName: e.target.value })} placeholder="First name" />
                      </div>
                      <div className="form-group">
                        <label>Other Names</label>
                        <input type="text" value={newClient.otherNames} onChange={(e) => setNewClient({ ...newClient, otherNames: e.target.value })} placeholder="Other names" />
                      </div>
                      <div className="form-group">
                        <label>Surname</label>
                        <input type="text" value={newClient.surname} onChange={(e) => setNewClient({ ...newClient, surname: e.target.value })} placeholder="Surname" />
                      </div>
                      <div className="form-group">
                        <label>KRA Pin</label>
                        <input type="text" value={newClient.pin} onChange={(e) => setNewClient({ ...newClient, pin: e.target.value })} placeholder="KRA PIN" />
                      </div>
                      <div className="form-group">
                        <label>Mobile</label>
                        <input type="tel" value={newClient.mobile} onChange={(e) => setNewClient({ ...newClient, mobile: e.target.value })} placeholder="Mobile" />
                      </div>
                      <div className="form-group full-width">
                        <label>Email</label>
                        <input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} placeholder="Email" />
                      </div>
                    </div>
                    <div className="modal-actions">
                      <button type="button" className="btn-secondary" onClick={() => setShowNewClientForm(false)}>Cancel</button>
                      <button type="button" className="btn-primary" onClick={handleCreateClient} disabled={savingClient}>
                        {savingClient ? 'Saving...' : 'Save Client'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="attachments">
                  <label className="attachments-label">Attach Documents (optional)</label>
                  <input
                    type="file"
                    id="proposalDocs"
                    className="file-input"
                    multiple
                    onChange={handleFileChange}
                  />
                  <label htmlFor="proposalDocs" className="file-input-btn">+ Choose Files</label>
                  {attachedFiles.length > 0 && (
                    <ul className="file-list">
                      {attachedFiles.map((f, idx) => (
                        <li key={idx}>
                          <span className="file-name">📎 {f.name}</span>
                          <button type="button" className="file-remove" onClick={() => removeAttachedFile(idx)}>×</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setProposalStep(2)}>
                    ← Back
                  </button>
                  <button type="button" className="btn-primary" onClick={handleFinalSubmit} disabled={!proposalForm.clientOID || submittingProposal}>
                    {submittingProposal ? 'Saving...' : editingProposalOID ? 'Save Changes' : 'Create Proposal'}
                  </button>
                </div>
              </div>
            )}

            {proposalStep === 4 && (
              <div className="premium-step">
                <div className="success-state">
                  <div className="success-badge">✓</div>
                  <h3>Proposal Created Successfully</h3>
                  {proposalResult?.policyNumber && (
                    <p className="success-ref">Policy No: <strong>{proposalResult.policyNumber}</strong></p>
                  )}
                  <p>Choose how you'd like to proceed.</p>
                </div>

                <div className="step4-options">
                  <button type="button" className="option-card" onClick={handleProceedToPayment} disabled={payingProposal}>
                    <span className="option-icon">💳</span>
                    <span className="option-title">{payingProposal ? 'Processing...' : 'Proceed to Payment'}</span>
                    <span className="option-sub">Pay the premium now</span>
                  </button>
                  <button type="button" className="option-card" onClick={handleDownloadDebitNote} disabled={payingProposal}>
                    <span className="option-icon">📄</span>
                    <span className="option-title">Download Debit Note</span>
                    <span className="option-sub">Save the debit note as PDF</span>
                  </button>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={closeProposalForm} disabled={payingProposal}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderActivePolicies = () => (
    <div className="policies-section">
      <div className="section-header">
        <h2>Active Policies</h2>
      </div>
      <div className="proposals-toolbar">
        <input
          type="text"
          className="proposals-search"
          value={policySearch}
          onChange={(e) => setPolicySearch(e.target.value)}
          placeholder="Search by client name, proposal OID or policy no."
        />
        <div className="date-filter">
          <label>From</label>
          <input type="date" value={policyDateFrom} onChange={(e) => setPolicyDateFrom(e.target.value)} />
          <label>To</label>
          <input type="date" value={policyDateTo} onChange={(e) => setPolicyDateTo(e.target.value)} />
          {(policySearch || policyDateFrom || policyDateTo) && (
            <button
              className="btn-secondary btn-small"
              onClick={() => { setPolicySearch(''); setPolicyDateFrom(''); setPolicyDateTo(''); }}
            >
              Clear
            </button>
          )}
        </div>
      </div>
      <div className="card">
        {loadingPolicies ? (
          <p className="field-hint">Loading policies...</p>
        ) : policiesList.length === 0 ? (
          <p className="field-hint">No active policies found.</p>
        ) : filteredPolicies.length === 0 ? (
          <p className="field-hint">No policies match your search/filter.</p>
        ) : (
          <div className="proposals-list">
            {filteredPolicies.map((p) => (
              <div key={p.OID} className="proposal-item">
                <div className="proposal-header">
                  <h4>{p.ProductDescription}{p.ItemName ? ` — ${p.ItemName}` : ''}</h4>
                  <span className="status-badge active">Active</span>
                </div>
                <p className="proposal-description">
                  Policy: {p.PolicyNo || '-'} · Proposal: {p.ProposalNo || '-'} · OID: {p.OID}
                </p>
                <div className="proposal-details">
                  <span>📅 {formatDate(p.CommencementDate)} → {formatDate(p.ExpiryDate)}</span>
                  <span>🗓 {p.PeriodOfCover || 0} days</span>
                  <span>🕒 Date Done: {formatDate(p.DateDone)}</span>
                  <span>💰 Sum Insured: KES {formatMoney(p.SumInsured)}</span>
                  <span>Premium: KES {formatMoney(p.GrossPremium)}</span>
                </div>
                <div className="proposal-actions">
                  <button className="btn-small" onClick={() => handleViewPolicyDetails(p)}>
                    View Policy Details
                  </button>
                  <button className="btn-small" onClick={() => handleDownloadPolicyDocument(p)}>
                    Download Policy Document
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showPolicyDetails && (
        <div className="modal-overlay" onClick={() => setShowPolicyDetails(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <h3>Policy Details</h3>
                {policyDetails && <span className="step-indicator">{policyDetails.PolicyNo || `OID ${policyDetails.OID}`}</span>}
              </div>
              <button className="modal-close" onClick={() => setShowPolicyDetails(false)}>×</button>
            </div>
            <div className="premium-step">
              {loadingPolicyDetails ? (
                <p className="field-hint">Loading policy details...</p>
              ) : policyDetails ? (
                <>
                  <div className="details-section-title">Policy</div>
                  <div className="details-grid">
                    <div><span>Policy No</span><strong>{policyDetails.PolicyNo || '-'}</strong></div>
                    <div><span>Proposal No</span><strong>{policyDetails.ProposalNo || '-'}</strong></div>
                    <div><span>Product</span><strong>{policyDetails.ProductDescription || '-'}</strong></div>
                    <div><span>Insured Item</span><strong>{policyDetails.ItemName || '-'}</strong></div>
                    <div><span>Cover Option</span><strong>{coverLabelFromValue(policyDetails.CoverOption)}</strong></div>
                    <div><span>Date Done</span><strong>{formatDate(policyDetails.DateDone)}</strong></div>
                  </div>

                  <div className="details-section-title">Client</div>
                  <div className="details-grid">
                    <div><span>Name</span><strong>{[policyDetails.FirstName, policyDetails.OtherNames, policyDetails.Surname].filter(Boolean).join(' ') || '-'}</strong></div>
                    <div><span>Identification</span><strong>{policyDetails.ClientIdNo || policyDetails.IDNO || '-'}</strong></div>
                    <div><span>Mobile</span><strong>{policyDetails.ClientMobile || '-'}</strong></div>
                    <div><span>Email</span><strong>{policyDetails.ClientEmail || '-'}</strong></div>
                  </div>

                  <div className="details-section-title">Cover</div>
                  <div className="details-grid">
                    <div><span>Commencement</span><strong>{formatDate(policyDetails.CommencementDate)}</strong></div>
                    <div><span>Expiry</span><strong>{formatDate(policyDetails.ExpiryDate)}</strong></div>
                    <div><span>Cover Period</span><strong>{policyDetails.PeriodOfCover || 0} days</strong></div>
                    <div><span>Sum Insured</span><strong>KES {formatMoney(policyDetails.SumInsured)}</strong></div>
                  </div>

                  <div className="details-section-title">Premium</div>
                  <div className="details-grid">
                    <div><span>Basic Premium</span><strong>KES {formatMoney(policyDetails.BasicPremium)}</strong></div>
                    <div><span>Stamp Duty</span><strong>KES {formatMoney(policyDetails.StampDuty)}</strong></div>
                    <div><span>PHCF</span><strong>KES {formatMoney(policyDetails.PHCF)}</strong></div>
                    <div><span>Training Levy</span><strong>KES {formatMoney(policyDetails.TrainingLevy)}</strong></div>
                    <div><span>Commission</span><strong>KES {formatMoney(policyDetails.GrossCommission)}</strong></div>
                    <div><span>Net Premium</span><strong>KES {formatMoney(policyDetails.NetPremium)}</strong></div>
                    <div><span>Gross Premium</span><strong>KES {formatMoney(policyDetails.GrossPremium)}</strong></div>
                  </div>

                  <div className="modal-actions">
                    <button className="btn-secondary" onClick={() => setShowPolicyDetails(false)}>Close</button>
                    <button className="btn-primary" onClick={() => handleDownloadPolicyDocument(policyDetails)}>Download Policy Document</button>
                  </div>
                </>
              ) : (
                <p className="field-hint">No details available.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const clientFullName = (p) => [p.FirstName, p.OtherNames, p.Surname].filter(Boolean).join(' ') || '-';

  const downloadCsv = (fileName, headers, rows) => {
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers, ...rows].map((r) => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Build the current report's title, columns, rows and totals row
  const buildReport = (rows) => {
    const sum = (key) => rows.reduce((acc, p) => acc + (Number(p[key]) || 0), 0);

    if (activeReport === 'commission') {
      return {
        title: 'Commission Statement Report',
        fileBase: 'CommissionStatement',
        columns: ['Policy No', 'Client', 'Sum Insured', 'Basic Premium', 'Commission', 'Net Commission', 'Date Done'],
        rows: rows.map((p) => [
          p.PolicyNo || '-', clientFullName(p), formatMoney(p.SumInsured),
          formatMoney(p.BasicPremium), formatMoney(p.GrossCommission), formatMoney(p.NetCommission), formatDate(p.DateDone)
        ]),
        foot: ['TOTAL', '', '', '', formatMoney(sum('GrossCommission')), formatMoney(sum('NetCommission')), '']
      };
    }
    // Default: Product Report
    return {
      title: 'Product Report',
      fileBase: 'ProductReport',
      columns: ['Policy No', 'Proposal No', 'Client', 'Product', 'Item', 'Commencement', 'Expiry', 'Sum Insured', 'Gross Premium', 'Date Done'],
      rows: rows.map((p) => [
        p.PolicyNo || '-', p.ProposalNo || '-', clientFullName(p), p.ProductDescription || '-', p.ItemName || '-',
        formatDate(p.CommencementDate), formatDate(p.ExpiryDate), formatMoney(p.SumInsured), formatMoney(p.GrossPremium), formatDate(p.DateDone)
      ]),
      foot: ['TOTAL', '', '', '', '', '', '', formatMoney(sum('SumInsured')), formatMoney(sum('GrossPremium')), '']
    };
  };

  const renderReports = () => {
    // Landing — choose a report
    if (!activeReport) {
      return (
        <div className="reports-section">
          <div className="section-header">
            <h2>Reports</h2>
          </div>
          <div className="card">
            <div className="reports-grid">
              <div className="report-item">
                <h4>Product Report</h4>
                <p>Production of policies with dates, sums insured and premiums. Filter by date and export as PDF or CSV.</p>
                <button className="btn-primary" onClick={() => setActiveReport('product')}>Open Report</button>
              </div>
              <div className="report-item">
                <h4>Commission Statement Report</h4>
                <p>Commission earned per policy with basic premium, gross and net commission. Filter by date and export.</p>
                <button className="btn-primary" onClick={() => setActiveReport('commission')}>Open Report</button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Report page
    const filtered = reportData.filter((p) => {
      if (reportDateFrom || reportDateTo) {
        if (!p.DateDone) return false;
        const done = new Date(p.DateDone);
        if (reportDateFrom && done < new Date(`${reportDateFrom}T00:00:00`)) return false;
        if (reportDateTo && done > new Date(`${reportDateTo}T23:59:59`)) return false;
      }
      return true;
    });

    const report = buildReport(filtered);
    const rangeLabel = (reportDateFrom || reportDateTo)
      ? `Period: ${reportDateFrom || '…'} to ${reportDateTo || '…'}`
      : 'All records';

    const exportPdf = () =>
      generateReportPdf({
        title: report.title,
        subtitle: `${rangeLabel}  ·  Agent: ${user?.name || ''}`,
        head: report.columns,
        body: report.rows,
        foot: report.foot,
        fileName: `${report.fileBase}-${new Date().toISOString().slice(0, 10)}.pdf`
      });

    const exportCsv = () =>
      downloadCsv(`${report.fileBase}-${new Date().toISOString().slice(0, 10)}.csv`, report.columns, report.rows);

    return (
      <div className="reports-section">
        <div className="section-header">
          <h2>{report.title}</h2>
        </div>

        <div className="proposals-toolbar">
          <button className="btn-secondary btn-small" onClick={() => { setActiveReport(null); setReportDateFrom(''); setReportDateTo(''); }}>
            ← Back to reports
          </button>
          <div className="date-filter">
            <label>From</label>
            <input type="date" value={reportDateFrom} onChange={(e) => setReportDateFrom(e.target.value)} />
            <label>To</label>
            <input type="date" value={reportDateTo} onChange={(e) => setReportDateTo(e.target.value)} />
            {(reportDateFrom || reportDateTo) && (
              <button className="btn-secondary btn-small" onClick={() => { setReportDateFrom(''); setReportDateTo(''); }}>Clear</button>
            )}
            <button className="btn-primary btn-small" onClick={exportPdf} disabled={report.rows.length === 0}>Export PDF</button>
            <button className="btn-primary btn-small" onClick={exportCsv} disabled={report.rows.length === 0}>Export CSV</button>
          </div>
        </div>

        <div className="card">
          {loadingReport ? (
            <p className="field-hint">Loading report...</p>
          ) : report.rows.length === 0 ? (
            <p className="field-hint">No records for the selected period.</p>
          ) : (
            <div className="report-table-wrap">
              <table className="policies-table">
                <thead>
                  <tr>{report.columns.map((c) => <th key={c}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {report.rows.map((row, i) => (
                    <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>{report.foot.map((cell, j) => <th key={j}>{cell}</th>)}</tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const startEditProfile = () => {
    setProfileForm({
      dealerFullName: profile?.DealerFullName || '',
      dealerName: profile?.Dealer_name || '',
      pin: profile?.Pin || '',
      mobile: profile?.Mobile || '',
      email: profile?.Email || '',
      physicalAddress: profile?.PhysicalAddress || '',
      postalAddress: profile?.PostalAddress || ''
    });
    setEditingProfile(true);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await fetch('https://uat.definiteassurance.com/api/marine/agent-profile/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oid: user?.oid, ...profileForm })
      });
      const data = await response.json();
      if (data.success) {
        showToast('Profile updated successfully', 'success');
        setEditingProfile(false);
        setProfileRefresh((v) => v + 1);
      } else {
        showToast(data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast('Error updating profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleUploadProfileDocs = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;
    setUploadingProfileDocs(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('documents', f));
      const response = await fetch(
        `https://uat.definiteassurance.com/api/marine/profile/upload?agentCode=${encodeURIComponent(user.oid)}`,
        { method: 'POST', body: formData }
      );
      const data = await response.json();
      if (data.success) {
        showToast('Documents uploaded successfully', 'success');
        setProfileRefresh((v) => v + 1);
      } else {
        showToast(data.message || 'Failed to upload documents');
      }
    } catch (error) {
      console.error('Error uploading profile documents:', error);
      showToast('Error uploading documents');
    } finally {
      setUploadingProfileDocs(false);
    }
  };

  const profileFileUrl = (name, download) =>
    `https://uat.definiteassurance.com/api/marine/documents/file?agentCode=${encodeURIComponent(user?.oid)}&proposalOID=MyDocuments&name=${encodeURIComponent(name)}${download ? '&download=1' : ''}`;

  const renderProfile = () => (
    <div className="profile-section">
      <div className="section-header">
        <h2>My Profile</h2>
      </div>

      {loadingProfile ? (
        <div className="card"><p className="field-hint">Loading profile...</p></div>
      ) : !profile ? (
        <div className="card"><p className="field-hint">No profile found for this account.</p></div>
      ) : (
        <>
          <div className="card">
            <div className="profile-card-header">
              <h3>Agency Details</h3>
              {!editingProfile ? (
                <button className="btn-secondary btn-small" onClick={startEditProfile}>Edit</button>
              ) : (
                <div className="profile-edit-actions">
                  <button className="btn-secondary btn-small" onClick={() => setEditingProfile(false)}>Cancel</button>
                  <button className="btn-primary btn-small" onClick={handleSaveProfile} disabled={savingProfile}>
                    {savingProfile ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {!editingProfile ? (
              <div className="details-grid">
                <div><span>Agent Code</span><strong>{profile.Dealer_code || '-'}</strong></div>
                <div><span>Status</span><strong>{Number(profile.Approved) === 1 ? 'Approved' : 'Pending'}</strong></div>
                <div><span>Full Name</span><strong>{profile.DealerFullName || '-'}</strong></div>
                <div><span>Agency Name</span><strong>{profile.Dealer_name || '-'}</strong></div>
                <div><span>KRA Pin</span><strong>{profile.Pin || '-'}</strong></div>
                <div><span>Mobile</span><strong>{profile.Mobile || '-'}</strong></div>
                <div><span>Email</span><strong>{profile.Email || '-'}</strong></div>
                <div><span>Physical Address</span><strong>{profile.PhysicalAddress || '-'}</strong></div>
                <div><span>Postal Address</span><strong>{profile.PostalAddress || '-'}</strong></div>
              </div>
            ) : (
              <div className="profile-form-grid">
                <div className="form-group">
                  <label>Full Name</label>
                  <input type="text" value={profileForm.dealerFullName} onChange={(e) => setProfileForm({ ...profileForm, dealerFullName: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Agency Name</label>
                  <input type="text" value={profileForm.dealerName} onChange={(e) => setProfileForm({ ...profileForm, dealerName: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>KRA Pin</label>
                  <input type="text" value={profileForm.pin} onChange={(e) => setProfileForm({ ...profileForm, pin: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Mobile</label>
                  <input type="tel" value={profileForm.mobile} onChange={(e) => setProfileForm({ ...profileForm, mobile: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Physical Address</label>
                  <input type="text" value={profileForm.physicalAddress} onChange={(e) => setProfileForm({ ...profileForm, physicalAddress: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Postal Address</label>
                  <input type="text" value={profileForm.postalAddress} onChange={(e) => setProfileForm({ ...profileForm, postalAddress: e.target.value })} />
                </div>
              </div>
            )}
          </div>

          <div className="card">
            <div className="profile-card-header">
              <h3>My Documents</h3>
              <div>
                <input type="file" id="profileDocsInput" className="file-input" multiple onChange={handleUploadProfileDocs} disabled={uploadingProfileDocs} />
                <label htmlFor="profileDocsInput" className="file-input-btn">
                  {uploadingProfileDocs ? 'Uploading...' : '+ Upload Documents'}
                </label>
              </div>
            </div>

            {profileDocs.length === 0 ? (
              <p className="field-hint">No documents uploaded yet.</p>
            ) : (
              <div className="doc-file-list">
                {profileDocs.map((file) => (
                  <div key={file.name} className="doc-file-item">
                    <span className="doc-file-name">📄 {file.name}</span>
                    <div className="doc-file-actions">
                      <button className="btn-small" onClick={() => window.open(profileFileUrl(file.name, false), '_blank', 'noopener')}>View</button>
                      <button className="btn-small btn-small-primary" onClick={() => {
                        const a = document.createElement('a');
                        a.href = profileFileUrl(file.name, true);
                        a.download = file.name;
                        document.body.appendChild(a); a.click(); a.remove();
                      }}>Download</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderSettings = () => (
    <div className="settings-section">
      <div className="section-header">
        <h2>Settings</h2>
      </div>

      <div className="settings-grid">
        <div className="card settings-card">
          <h3>Appearance</h3>
          <div className="settings-row">
            <div className="settings-row-text">
              <p className="settings-row-title">Theme</p>
              <p className="settings-row-sub">Choose how the portal looks</p>
            </div>
            <div className="theme-toggle">
              <button
                className={`theme-option ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
              >☀️ Light</button>
              <button
                className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
              >🌙 Dark</button>
            </div>
          </div>
        </div>

        <div className="card settings-card">
          <h3>Renewal Alerts</h3>
          <div className="settings-row">
            <div className="settings-row-text">
              <p className="settings-row-title">Email Alerts</p>
              <p className="settings-row-sub">Renewal reminders by email</p>
            </div>
            <label className="switch">
              <input type="checkbox" checked={emailAlerts} onChange={(e) => setEmailAlerts(e.target.checked)} />
              <span className="slider"></span>
            </label>
          </div>
          <div className="settings-row">
            <div className="settings-row-text">
              <p className="settings-row-title">SMS Alerts</p>
              <p className="settings-row-sub">Renewal reminders by SMS</p>
            </div>
            <label className="switch">
              <input type="checkbox" checked={smsAlerts} onChange={(e) => setSmsAlerts(e.target.checked)} />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        <div className="card settings-card">
          <h3>Account</h3>
          <div className="settings-row">
            <div className="settings-row-text">
              <p className="settings-row-title">{user?.name || 'User'}</p>
              <p className="settings-row-sub">{user?.email || ''}</p>
            </div>
          </div>
          <button className="btn-danger settings-logout" onClick={handleLogout}>Logout</button>
        </div>

        <div className="card settings-card">
          <h3>Support</h3>
          <div className="settings-row">
            <div className="settings-row-text">
              <p className="settings-row-title">Call / WhatsApp</p>
              <p className="settings-row-sub"><a href="tel:0708247247">0708 247 247</a></p>
            </div>
            <a className="btn-secondary btn-small" href="https://wa.me/254708247247" target="_blank" rel="noopener noreferrer">WhatsApp</a>
          </div>
          <div className="settings-row">
            <div className="settings-row-text">
              <p className="settings-row-title">Email</p>
              <p className="settings-row-sub"><a href="mailto:underwriting@definiteassurance.com">underwriting@definiteassurance.com</a></p>
            </div>
          </div>
        </div>
      </div>

      <div className="card settings-about-card">
        <img src="/definitelogo.png" alt="Definite" className="about-logo" />
        <p className="about-version">System Version v1</p>
        <p>© {new Date().getFullYear()} Definite Assurance Company Limited. All rights reserved.</p>
        <p>Developed and Maintained by <a href="https://victrie.com" target="_blank" rel="noopener noreferrer">Victrie.com</a></p>
        <p><a href="https://www.definiteassurance.com" target="_blank" rel="noopener noreferrer">Privacy Policy</a></p>
      </div>
    </div>
  );

  const renderContent = () => {
    switch(activeTab) {
      case 'overview':
        return renderOverview();
      case 'documents':
        return renderDocuments();
      case 'new-proposals':
        return renderNewProposals();
      case 'active-policies':
        return renderActivePolicies();
      case 'reports':
        return renderReports();
      case 'profile':
        return renderProfile();
      case 'settings':
        return renderSettings();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="dashboard">
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <span className="toast-icon">{toast.type === 'error' ? '⚠️' : '✓'}</span>
          <span>{toast.message}</span>
        </div>
      )}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <img src="/definitelogo.png" alt="Logo" className="sidebar-logo" />
          <h2>Definite</h2>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => navTo('overview')}
          >
            <span className="nav-icon">📊</span>
            <span>Overview</span>
          </button>

          <div className="nav-menu-group">
            <button
              className={`nav-item nav-parent ${expandedMenu === 'proposals' ? 'expanded' : ''}`}
              onClick={() => handleMenuClick('proposals')}
            >
              <span className="nav-icon">📋</span>
              <span>Proposal Management</span>
              <span className="menu-arrow">▼</span>
            </button>
            {expandedMenu === 'proposals' && (
              <div className="submenu">
                <button
                  className={`submenu-item ${activeTab === 'new-proposals' ? 'active' : ''}`}
                  onClick={() => navTo('new-proposals')}
                >
                  <span className="submenu-icon">📝</span>
                  New Proposals
                </button>
                <button
                  className={`submenu-item ${activeTab === 'active-policies' ? 'active' : ''}`}
                  onClick={() => navTo('active-policies')}
                >
                  <span className="submenu-icon">✓</span>
                  Active Policies
                </button>
              </div>
            )}
          </div>

          <button
            className={`nav-item ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => navTo('documents')}
          >
            <span className="nav-icon">📁</span>
            <span>Documents</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => navTo('reports')}
          >
            <span className="nav-icon">📈</span>
            <span>Reports</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => navTo('profile')}
          >
            <span className="nav-icon">👤</span>
            <span>My Profile</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => navTo('settings')}
          >
            <span className="nav-icon">⚙️</span>
            <span>Settings</span>
          </button>
        </nav>
      </aside>

      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      <main className="main-content">
        <header className="dashboard-header">
          <button
            className="menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            ☰
          </button>
          <h1>Definite Assurance Marine Portal</h1>
          <div className="header-right">
            <span className="user-name">{user?.name || 'User'}</span>
            <button className="btn-logout" onClick={handleLogout}>Logout</button>
          </div>
        </header>

        <div className="content">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}
